import type { Asset } from "~/ui-rtk/api/assetsApi"
import type { OverlayDoc } from "~/ui-rtk/api/adForgeApi"

export enum ImageCategory {
  ON_BODY = "On Body",
  LIFESTYLE = "Lifestyle",
  LAYDOWN = "Laydown",
  PRODUCT_DETAIL = "Product Detail",
  FABRIC_DETAIL = "Fabric Detail",
  OTHER = "Other",
}

export interface ScrapedImage {
  id: string
  url: string
  thumbnailUrl?: string
  type: "image" | "video" | "gif"
  base64?: string
  category: ImageCategory
  color?: string
  selected: boolean
  parentId?: string
  sourceUrl?: string
  assetId?: string // Asset ID from asset management system
  metadata?: Record<string, unknown> | null
  width?: number | null
  height?: number | null
  perceptualHash?: string | null
  /** Number of AI children generated from this asset. Marks an original that
   *  already has variants hanging off it. */
  variantCount?: number
  /**
   * Layout assigned to this card (already rescaled to the media's dims).
   * Rendered as a LIVE overlay in the editor previews; baked into the media
   * only when the ad is created (save draft).
   */
  overlayDoc?: OverlayDoc
  overlayLayoutId?: string
  /**
   * Per-placement arrangements: a slot listed here uses ITS doc (edited at
   * the placement's frame) instead of re-solving `overlayDoc` at that ratio.
   */
  overlayDocByPlacement?: Partial<
    Record<"landscape" | "portrait" | "stories" | "square", OverlayDoc>
  >
}

export const assetToScrapedImage = (asset: Asset): ScrapedImage => ({
  id: asset.id,
  url: asset.url || asset.thumbnailUrl || "",
  thumbnailUrl: asset.thumbnailUrl,
  type: asset.mediaType === "VIDEO" ? "video" : "image",
  category: ImageCategory.OTHER,
  color: "Default",
  selected: true,
  assetId: asset.id,
  // Lineage pointer — lets the editor recognise a selected AI variant as a
  // child and resolve it back to its original card (main + placement slots).
  parentId: asset.parentAssetId ?? undefined,
  width: asset.width,
  height: asset.height,
  perceptualHash: asset.perceptualHash,
  ...(asset.variantCount && { variantCount: asset.variantCount }),
  metadata: (asset as { metadata?: Record<string, unknown> | null }).metadata,
})

/**
 * True when an asset is an AI-generated child (it has a parent and a
 * `generatedBy` marker — enhance / extend / motion). Such an asset selected as
 * a card should resolve back to its original source image, with the sibling
 * variants auto-filling the placement slots.
 */
export const isGenAiChild = (
  asset?: {
    parentId?: string
    metadata?: Record<string, unknown> | null
  } | null,
): boolean =>
  !!asset?.parentId && String(asset?.metadata?.generatedBy ?? "").length > 0

/**
 * True when an asset is itself an AI-enhanced/upscaled output (a GEN_AI child
 * tagged `generatedBy: ai_enhance`). Used to avoid re-enhancing a card that was
 * already swapped to its enhanced version, and to suppress the low-res warning
 * on it (re-running enhance won't help — it's already the upscaled result).
 */
export const isEnhancedAsset = (
  asset?: { metadata?: Record<string, unknown> | null } | null,
): boolean => String(asset?.metadata?.generatedBy ?? "").includes("enhance")

/**
 * True when an image asset is AVIF. The AdForge image-edit pipeline (stock
 * `sharp`) can't decode AVIF, so these are excluded from enhancement the same
 * way videos are — they still display normally as cards, they just never get
 * sent to the image-gen job. Detected from the asset URL's extension: uploads
 * preserve the original extension in the S3 key (e.g. `…/<uuid>.avif`).
 */
export const isAvifImage = (
  asset?: { url?: string; sourceUrl?: string } | null,
): boolean =>
  [asset?.url, asset?.sourceUrl].some(u => /\.avif(?:$|[?#])/i.test(u ?? ""))

export interface ScrapedReview {
  id: string
  author: string
  text: string
  rating: number
  selected: boolean
  sourceUrl?: string
}

export interface ScrapedText {
  id: string
  content: string
  type: "heading" | "paragraph" | "list_item" | "other"
  selected: boolean
  sourceUrl?: string
}

export interface BrandInfo {
  campaignType: "product" | "collection" | "brand"
  productUrl?: string
  homepageUrl?: string
  aboutUrl?: string
  tags: string[]
  productDescription: string
  includeReviews: boolean
  brandFont: string
  logoUrl?: string
  includeEndcard: boolean
}

export type AdType = "carousel" | "slideshow" | "motion"

export interface GeneratedAsset {
  id: string
  type: "image" | "video"
  url: string
  format: "1:1" | "9:16"
  description: string
  thumbnail?: string
  status: "pending" | "generating" | "completed" | "failed"
}

export interface ScrapeResult {
  images: ScrapedImage[]
  reviews: ScrapedReview[]
  text: ScrapedText[]
}

export type VeoModel = "veo-3.1-fast-generate-001"

export type VeoDuration = "4" | "6" | "8"
export type VeoResolution = "720p" | "1080p" | "4k"
export type VeoPersonGeneration = "allow_all" | "allow_adult"

export interface MotionConfig {
  prompt: string
  negativePrompt: string
  model: VeoModel
  durationSeconds: VeoDuration
  resolution: VeoResolution
  personGeneration: VeoPersonGeneration
  numberOfVideos: number
  seed: string
  enhancePrompt: boolean
}

export interface SupercutClip {
  id: string
  videoUrl: string
  thumbnailUrl: string
  duration: number
  speed: number
  order: number
}

export interface GeneratedAdCopy {
  meta: {
    primary: string[]
    headline: string[]
    description: string[]
  }
  tiktok: {
    caption: string[]
  }
  youtube: {
    headline: string[]
    description: string[]
  }
  pmax: {
    headline: string[]
    longHeadline: string[]
    description: string[]
  }
}

export interface AssetGeneratorState {
  orderedAssets: ScrapedImage[]
  motionConfigs: Record<string, MotionConfig>
  generatedVideos: Record<string, Record<string, string>>
  generatedCopy?: GeneratedAdCopy
}

export type { CallToAction } from "~/ui-rtk/api/types/paid-media.types"

export interface PlacementAssets {
  /** 1.91:1 — FB Feed, IG Stream, Search, Marketplace */
  landscape?: ScrapedImage
  /** 4:5 — IG Explore Home, IG Profile Feed */
  portrait?: ScrapedImage
  /** 9:16 — Stories, Reels */
  stories?: ScrapedImage
  /** 1:1 — Audience Network, fallback */
  square?: ScrapedImage
  /** @deprecated use `portrait` (kept for backward compatibility) */
  feed?: ScrapedImage
}

export interface AdLauncherAd {
  id: string
  template: "single" | "carousel"
  name: string
  assets: ScrapedImage[]
  creative: {
    primaryText: string
    headline: string
    description: string
    destinationUrl: string
    callToAction: string
    carouselCards?: Array<{
      assetIndex: number
      headline: string
      description: string
      link?: string
    }>
  }
  tracking: Array<{ key: string; value: string }>
  placementAssets?: PlacementAssets
  /**
   * Carousel-only: per-card placement variants keyed by the carousel card's
   * asset id. Each entry holds the same shape as `placementAssets`, allowing
   * each card to define its own per-placement crops/uploads.
   */
  placementAssetsByCard?: Record<string, PlacementAssets>
  pageId?: string
  adsetOverrides?: string[]
}

export interface AdLauncherState {
  ads: AdLauncherAd[]
  globalAdSetIds: string[]
  phase: "build" | "review"
}

export interface SavedCampaign {
  id: string
  name: string
  updatedAt: number
  step: number
  brandInfo: BrandInfo
  images: ScrapedImage[]
  reviews: ScrapedReview[]
  text: ScrapedText[]
  selectedAdTypes?: AdType[]
  assetGeneratorState?: AssetGeneratorState
}
